import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

interface LayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Overview', href: '/' },
  { name: 'Requests', href: '/requests' },
]

const adminNavigation = [
  { name: 'Users', href: '/admin/users' },
  { name: 'Analytics', href: '/admin/analytics' },
]

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { logout, isAdmin } = useAuth()

  return (
    <div className="min-h-screen bg-noir-primary">
      <nav className="bg-noir-secondary shadow-xs border-b border-border" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="shrink-0 flex items-center">
                <Link to="/" className="text-xl font-bold text-text-primary focus:outline-hidden focus:ring-2 focus:ring-accent-cyan rounded-sm">
                  <span className="text-accent-cyan">Code</span>Vet
                </Link>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-4" role="navigation" aria-label="Primary navigation">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-hidden focus:ring-2 focus:ring-accent-cyan focus:ring-offset-2 focus:ring-offset-noir-secondary ${
                        isActive
                          ? 'text-accent-cyan'
                          : 'text-text-secondary hover:text-text-primary'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.name}
                    </Link>
                  )
                })}
                {isAdmin && (
                  <>
                    <div className="border-l border-border mx-2"></div>
                    {adminNavigation.map((item) => {
                      const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-hidden focus:ring-2 focus:ring-accent-cyan focus:ring-offset-2 focus:ring-offset-noir-secondary ${
                            isActive
                              ? 'text-accent-cyan'
                              : 'text-text-secondary hover:text-text-primary'
                          }`}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {item.name === 'Users' ? (
                            <svg
                              className="h-4 w-4 mr-1.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                          ) : item.name === 'Analytics' ? (
                            <svg
                              className="h-4 w-4 mr-1.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              />
                            </svg>
                          ) : null}
                          {item.name}
                        </Link>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={logout}
                className="text-sm text-text-secondary hover:text-text-primary px-3 py-2 rounded-md transition-colors focus:outline-hidden focus:ring-2 focus:ring-accent-cyan focus:ring-offset-2 focus:ring-offset-noir-secondary"
                aria-label="Sign out of CodeVet"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" id="main-content" role="main">
        {children}
      </main>
    </div>
  )
}
